import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useUserRole } from '@/hooks/useUserRole';
import { ResultRow } from '@/components/customers/ResultRow';
import type { CustomerListItem, CustomerSegment, CustomerSort, StoreListItem } from '@/api/customers';
import { cacheGet, cacheSet } from '@/lib/swrCache';
import { useComposer } from '@/components/comms/ComposerProvider';
import axios from 'axios';
import '@/styles/screens.css';

const PAGE_SIZE = 25;

type Status = 'loading' | 'ready' | 'error';

// Segment filter chips (SPEC-2 §3): All / Wholesale / Added in store.
const SEGMENTS: { key: CustomerSegment | undefined; label: string }[] = [
  { key: undefined, label: 'All' },
  { key: 'wholesale', label: 'Wholesale' },
  { key: 'store_added', label: 'Added in store' },
];

const SORTS: { key: CustomerSort; label: string }[] = [
  { key: 'name', label: 'Name A–Z' },
  { key: 'newest', label: 'Recently added' },
  { key: 'recent', label: 'Last transaction' },
  { key: 'spend12_desc', label: 'Spend (12 mo) high–low' },
  { key: 'spend12_asc', label: 'Spend (12 mo) low–high' },
  { key: 'total_desc', label: 'Total spend' },
];

/**
 * Customers (SPEC §7, SPEC-2 §3): the store-scoped list with a toolbar — segment
 * chips, sort order, an office-only store filter — and page-based navigation.
 * RLS scopes rows to the caller's store(s); office users see all stores.
 */
export function CustomersListPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { seesAll } = useUserRole();

  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<Status>('loading');
  const [segment, setSegment] = useState<CustomerSegment | undefined>(undefined);
  const [sort, setSort] = useState<CustomerSort>('name');
  const [storeCode, setStoreCode] = useState('');
  const [page, setPage] = useState(0);
  const [allStores, setAllStores] = useState<StoreListItem[]>([]);
  // Multi-select for batch comms; selections persist across pages/filters.
  const [selected, setSelected] = useState<Map<string, CustomerListItem>>(new Map());
  const composer = useComposer();
  const [searchParams] = useSearchParams();
  // Arrived via Home → "Email customers": show the how-to hint until they select.
  const [composeHint, setComposeHint] = useState(searchParams.get('compose') === '1');

  // Guard so overlapping loads can't race an older response over a newer one.
  const loadSeq = useRef(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadPage = useCallback(
    async (pageIndex: number, signal?: AbortSignal) => {
      const seq = ++loadSeq.current;
      const cacheKey = `custlist:${segment ?? 'all'}:${sort}:${storeCode || '*'}:${pageIndex}`;
      // Stale-while-revalidate: paint the last-seen copy instantly, refresh behind.
      const cached = cacheGet<{ results: CustomerListItem[]; total: number }>(cacheKey);
      if (cached) {
        setItems(cached.results);
        setTotal(cached.total);
        setStatus('ready');
      } else {
        setStatus('loading');
      }
      try {
        const res = await api.list(PAGE_SIZE, pageIndex * PAGE_SIZE, signal, {
          segment,
          sort,
          store: storeCode || undefined,
        });
        if (seq !== loadSeq.current) return; // superseded by a newer load
        cacheSet(cacheKey, { results: res.results, total: res.total });
        setItems(res.results);
        setTotal(res.total);
        setStatus('ready');
      } catch (err) {
        if (axios.isCancel(err) || signal?.aborted) return;
        // A cached paint stays usable even if the refresh fails.
        if (seq === loadSeq.current && !cached) setStatus('error');
      }
    },
    [api, segment, sort, storeCode]
  );

  // (Re)load whenever a filter, the sort, or the page changes.
  useEffect(() => {
    const controller = new AbortController();
    void loadPage(page, controller.signal);
    return () => controller.abort();
  }, [loadPage, page]);

  // Office users get the store filter; load the store list once.
  useEffect(() => {
    if (!seesAll) return;
    api.stores().then(setAllStores).catch(() => setAllStores([]));
  }, [api, seesAll]);

  // Any filter change goes back to page 1.
  const resetTo = (fn: () => void) => {
    fn();
    setPage(0);
  };

  return (
    <div className="screen">
      <p className="screen-greeting">Customers</p>
      <h1 className="screen-title">Your customers</h1>

      <div className="list-toolbar">
        <div className="segment-chips" role="tablist" aria-label="Customer segment">
          {SEGMENTS.map((s) => (
            <button
              key={s.label}
              type="button"
              role="tab"
              aria-selected={segment === s.key}
              className={`segment-chip${segment === s.key ? ' segment-chip--active' : ''}`}
              onClick={() => resetTo(() => setSegment(s.key))}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="toolbar-controls">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            title={selected.size ? `Email the ${selected.size} selected customer${selected.size === 1 ? '' : 's'}` : 'Tick customers in the list, then email them together'}
            onClick={() => {
              if (selected.size === 0) { setComposeHint(true); return; }
              composer.openEmail([...selected.values()].map((c) => ({
                customer_id: c.customer_id,
                name: [c.first_name, c.last_name].filter(Boolean).join(' ') || '—',
                email: c.email,
              })));
            }}
          >
            <i className="ti ti-mail" aria-hidden="true" /> Email{selected.size ? ` (${selected.size})` : '…'}
          </button>
          {seesAll && allStores.length > 0 && (
            <select
              className="toolbar-select"
              aria-label="Filter by store"
              value={storeCode}
              onChange={(e) => resetTo(() => setStoreCode(e.target.value))}
            >
              <option value="">All stores</option>
              {allStores.map((s) => (
                <option key={s.store_id} value={s.code}>{s.name} ({s.code})</option>
              ))}
            </select>
          )}
          <select
            className="toolbar-select"
            aria-label="Sort order"
            value={sort}
            onChange={(e) => resetTo(() => setSort(e.target.value as CustomerSort))}
          >
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {composeHint && selected.size === 0 && (
        <p className="compose-hint">
          <i className="ti ti-mail" aria-hidden="true" /> Tick the customers you want to email
          using the checkboxes below, then press <strong>Email</strong>. Each person gets their
          own individual mail from your store&rsquo;s address.
        </p>
      )}

      {status === 'loading' && (
        <div className="result-list" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton-row" />)}
        </div>
      )}

      {status === 'error' && (
        <p className="state-msg state-msg--error">
          Couldn&rsquo;t load customers.{' '}
          <button className="btn btn--ghost" onClick={() => loadPage(page)}>Retry</button>
        </p>
      )}

      {status === 'ready' && items.length === 0 && (
        <p className="state-msg">No customers match this view.</p>
      )}

      {status === 'ready' && items.length > 0 && (
        <>
          <p className="list-count">
            {total} customer{total === 1 ? '' : 's'} · page {page + 1} of {totalPages}
          </p>
          <div className="result-list">
            {items.map((c) => (
              <div key={c.customer_id} className="select-row">
                <input
                  type="checkbox"
                  className="select-check"
                  aria-label={`Select ${c.first_name ?? ''} ${c.last_name ?? ''}`}
                  checked={selected.has(c.customer_id)}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = new Map(prev);
                      if (e.target.checked) next.set(c.customer_id, c);
                      else next.delete(c.customer_id);
                      return next;
                    });
                  }}
                />
                <ResultRow customer={c} onOpen={(id) => navigate(`/customers/${id}`)} />
              </div>
            ))}
          </div>

          {selected.size > 0 && (
            <div className="select-bar">
              <span>{selected.size} selected</span>
              <button
                className="btn btn--sm"
                onClick={() => composer.openEmail([...selected.values()].map((c) => ({
                  customer_id: c.customer_id,
                  name: [c.first_name, c.last_name].filter(Boolean).join(' ') || '—',
                  email: c.email,
                })))}
              >
                <i className="ti ti-mail" aria-hidden="true" /> Email selected
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => setSelected(new Map())}>Clear</button>
            </div>
          )}

          {totalPages > 1 && (
            <div className="pager">
              <button
                className="btn btn--ghost"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Previous
              </button>
              <span className="pager-status">Page {page + 1} of {totalPages}</span>
              <button
                className="btn btn--ghost"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
